<template>
  <span
    class="prepeers-skeleton"
    :class="variantClass"
    :style="skeletonStyle"
    aria-hidden="true" />
</template>

<script lang="ts" setup>
import { computed, type ComputedRef, type CSSProperties, type PropType } from 'vue'
import type { PrePeersSkeletonProps, PrePeersSkeletonVariant } from '~/types/PrePeersSkeleton'

/**
 * Définit les props du composant PrePeersSkeleton.
 */
const props: PrePeersSkeletonProps = defineProps({
  variant: {
    type: String as PropType<PrePeersSkeletonVariant>,
    default: 'text',
  },
  width: {
    type: String,
    default: undefined,
  },
  height: {
    type: String,
    default: undefined,
  },
  borderRadius: {
    type: String,
    default: undefined,
  },
})

const variantClass: ComputedRef<string> = computed(
  (): string => `prepeers-skeleton--${props.variant ?? 'text'}`,
)

const skeletonStyle: ComputedRef<CSSProperties> = computed((): CSSProperties => {
  const style: CSSProperties = {}
  if (props.width) style.width = props.width
  if (props.height) style.height = props.height
  if (props.borderRadius) style.borderRadius = props.borderRadius
  return style
})
</script>

<style scoped>
.prepeers-skeleton {
  --prepeers-skeleton-shimmer-duration: 2.8s;

  display: block;
  max-width: 100%;
  background-color: rgba(255, 255, 255, 0.05);
  background-image: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.06) 45%,
    rgba(255, 255, 255, 0.06) 55%,
    rgba(255, 255, 255, 0) 100%
  );
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: prepeers-skeleton-shimmer var(--prepeers-skeleton-shimmer-duration) ease-in-out
    infinite;
}

@media (prefers-reduced-motion: reduce) {
  .prepeers-skeleton {
    animation: none;
    background-image: none;
  }
}

.prepeers-skeleton--text {
  width: 100%;
  height: 1em;
  border-radius: var(--radius-sm);
}

.prepeers-skeleton--block {
  width: 100%;
  height: 100%;
  border-radius: var(--radius-md);
}

.prepeers-skeleton--circle {
  width: 100%;
  aspect-ratio: 1;
  border-radius: var(--radius-full);
}

@keyframes prepeers-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}
</style>
